/**
 * Upkeep Monitoring API Service
 * 
 * Service layer for Upkeep Budget Monitoring API calls
 */

import api from '../../../../auth/apiClient';
import { UPKEEP_MONITORING_API_CONFIG } from '../apiConfig';
import {
  UpkeepBudgetResponse,
  UpkeepBudgetFilters,
} from '../types';
import { trackDownload } from '../../../../activity';

/**
 * Service class for Upkeep Monitoring API operations
 */
export class UpkeepMonitoringApiService {
  /**
   * Get upkeep budget report data
   */
  static async getUpkeepBudgetReport(filters: UpkeepBudgetFilters): Promise<UpkeepBudgetResponse> {
    const params: Record<string, any> = {
      revision: filters.revision ?? 0,
      budget_year: filters.budget_year,
      budget_month: filters.budget_month,
      company_id: filters.company_id,
    };

    if (filters.estate_id) {
      params.estate_id = filters.estate_id;
    }
    if (filters.division_id) {
      params.division_id = filters.division_id;
    }
    if (filters.level) {
      params.level = filters.level;
    }

    const response = await api.get<UpkeepBudgetResponse>(
      UPKEEP_MONITORING_API_CONFIG.endpoints.UPKEEP_BUDGET_REPORT,
      { params }
    );
    return response.data;
  }

  /**
   * Stream download styled 2-sheet Excel report from Backend
   */
  static async downloadUpkeepBudgetExcel(filters: UpkeepBudgetFilters): Promise<void> {
    const params: Record<string, any> = {
      revision: filters.revision ?? 0,
      budget_year: filters.budget_year,
      budget_month: filters.budget_month,
      company_id: filters.company_id,
    };

    if (filters.estate_id) params.estate_id = filters.estate_id;
    if (filters.division_id) params.division_id = filters.division_id;

    const response = await api.get(
      `${UPKEEP_MONITORING_API_CONFIG.endpoints.UPKEEP_BUDGET_REPORT}/export-excel`,
      { params, responseType: 'blob' }
    );

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Upkeep_Budget_Monitoring_${filters.budget_year}_${filters.budget_month}.xlsx`;
    trackDownload('Upkeep Budget Monitoring', { budget_year: filters.budget_year, budget_month: filters.budget_month });
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export default UpkeepMonitoringApiService;
